// calc.ts — single calculation path for the standalone "Calculadora Modelo 130" page (F15b).
//
// SAME logic as the in-app estimator (FreelancerTaxEstimatorView.tsx): the net yield comes
// from the verified engine2025 EDS pipeline (computeDirectEstimationSimplified — 5% difficult
// justification capped at 2.000 €, param P-NEW-10-1) and the instalment is 20% of the floored
// net yield, exactly as the view computes `modelo130Result`. The parity test locks this module
// to the engine output so the public SEO page can never drift into hardcoded figures.

import { computeDirectEstimationSimplified } from '../../features/irpf/engine2025/blocks/economicActivity';
import { buildStateParamRegistry } from '../../features/irpf/engine2025/params/state';
import type { ParamRegistry } from '../../features/irpf/engine2025/params/registry';
import { roundHalfUp } from '../../features/irpf/engine2025/money';

let registry: ParamRegistry | null = null;
const getRegistry = (): ParamRegistry => (registry ??= buildStateParamRegistry());

/**
 * Instalment rate applied by the in-app estimator (FreelancerTaxEstimatorView.tsx:
 * `modelo130Result = netEconomicYield * 0.20`) — art. 110.1.a) RIRPF. Not a new fiscal
 * constant: it mirrors the app's existing one.
 */
export const MODELO_130_PAYMENT_RATE = 0.2;

export interface Modelo130Estimate {
    /** grossIncome − deductibleExpenses, whole euros (engine `difference`). */
    difference: number;
    /** Engine EDS difficult-justification expense (P-NEW-10-1), euros. */
    difficultJustification: number;
    /** Net yield floored at 0 (the 130 box does not admit a negative base), euros. */
    netYield: number;
    /** Estimated quarterly instalment: netYield × 20 %, euros. */
    payment: number;
}

/** Orientative Modelo 130 estimate — pure delegation to the engine EDS pipeline. */
export function computeModelo130Estimate(grossIncome: number, deductibleExpenses: number): Modelo130Estimate {
    const result = computeDirectEstimationSimplified(
        {
            grossIncome: roundHalfUp(grossIncome * 100),
            deductibleExpenses: roundHalfUp(deductibleExpenses * 100),
        },
        getRegistry()
    );
    // Same floor as the app: the 130 box does not admit a negative base (min instalment 0).
    const netYield = Math.max(0, result.netYieldReducedTotal) / 100;
    return {
        difference: result.difference / 100,
        difficultJustification: result.difficultJustificationExpense / 100,
        netYield,
        payment: netYield * MODELO_130_PAYMENT_RATE,
    };
}

export interface DifficultJustificationInfo {
    /** Percentage (0-100 scale) — engine param P-NEW-10-1. */
    pct: number;
    /** Cap in whole euros — engine param P-NEW-10-1. */
    maxAmount: number;
}

/** Engine parameters shown in the page copy (never hardcoded there). */
export function getDifficultJustificationInfo(): DifficultJustificationInfo {
    const { pct, maxAmount } = getRegistry().get<{ pct: number; maxAmount: number }>('P-NEW-10-1');
    return { pct, maxAmount: maxAmount / 100 };
}
